import { useLayoutEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { PROJECTS, wixImg } from "../data/projects";
import ContactSection from "../components/ContactSection";
import { useSEO } from "../lib/seo";

const HERO_IMG = "58bce5_e3bbb54eead74dad88844b540094933a~mv2.jpg";
const CORTEN_IMG = "58bce5_452d76dcecfc4b989470dacf582df8c1~mv2.png";
const WHY_RIGHT_IMG = "58bce5_1d0db1dce4594d909ac8418c22a406b5~mv2.jpeg";
const WIDE_IMG = "58bce5_0afc9dfa9a3e435abce68daf10b3af38~mv2.jpg";
const SERVICES_IMG = "58bce5_0f45eeda46cd411d9d99f6fd9b24eea8~mv2.jpg";

const scrollToContact = () => {
  const scroll = () =>
    document.querySelector("#kapcsolat")?.scrollIntoView({ behavior: "smooth", block: "start" });
  scroll();
  // Korrekció: a lazy módon betöltődő képek elcsúsztathatják az oldalt, ezért újra odagörgetünk
  setTimeout(scroll, 700);
  setTimeout(scroll, 1500);
};

const stats = [
  { value: "100+", label: "SIKERES PROJEKT" },
  { value: "10e +", label: "MEGTERVEZETT M²" },
  { value: "99%", label: "ELÉGEDETTSÉG" },
  { value: "20+", label: "ÉVES TAPASZTALAT" },
];

const steps = [
  { n: "1.", title: "Díjmentes konzultáció", text: "Átbeszéljük az elképzeléseidet, a telket és a lehetőségeket. Megmondjuk, mire számíthatsz már az elején." },
  { n: "2.", title: "Koncepció és költségkeret", text: "Nem rajzolunk vaktában. Először kialakul a koncepció és egy reális pénzügyi keret." },
  { n: "3.", title: "Engedélyezési és kiviteli tervek", text: "Minden dokumentáció elkészül, amire a hatóságoknak és a kivitelezőknek szükségük van." },
  { n: "4.", title: "Kivitelezési támogatás", text: "Segítünk kivitelezőt választani, és a megvalósítás során is melletted maradunk." },
];

const services = [
  "előzetes tanácsadás és megvalósíthatósági vizsgálat",
  "koncepciótervezés",
  "engedélyezési tervek",
  "belsőépítészeti tervek",
  "kiviteli tervek",
  "tervezői művezetés",
  "kivitelezési támogatás",
];

const principles = [
  { title: "FUNKCIONÁLIS TÉRSZERVEZÉS", text: "Hatékony alaprajzi struktúra és jól használható terek a mindennapi működéshez igazítva." },
  { title: "BELSŐÉPITTÉSZET ÉS ANYAGHASZNÁLAT", text: "Harmonikus térélmény, időtálló anyagválasztás és részletképzés, a funkcionalitás és esztétika egyensúlyával." },
  { title: "MŰSZAKI PRECIZITÁS - MEGVALÓSITHATÓSÁG", text: "Pontos kiviteli részletek, költséghatékony szerkezeti megoldások és kivitelezésre optimalizált tervezési folyamat." },
  { title: "FENNTARTHATÓ ÉS ENERGIA - TUDATOS", text: "Környezettudatos tervezési szemlélet, optimalizált energetikai koncepció és hosszú távon gazdaságos üzemeltetés." },
];

const Home = () => {
  const lakohazak = PROJECTS.lakohazak;
  useSEO(
    "New Idea Architect – Építész iroda Budapest | Tervezés, engedélyezés, megvalósítás",
    "Komplex építészeti tervezés családi házak, társasházak és középületek számára – az első gondolattól a kulcsrakész átadásig. Díjmentes konzultáció.",
    "/"
  );
  const headRef = useRef(null);
  const subRef = useRef(null);

  // A "KIEMELKEDŐ TERVEZÉSI MEGOLDÁSOK" sor betűméretét úgy állítjuk be,
  // hogy pontosan olyan széles legyen, mint a "Tervezési alapelvek" cím.
  useLayoutEffect(() => {
    const fit = () => {
      const head = headRef.current;
      const sub = subRef.current;
      if (!head || !sub) return;
      const cs = window.getComputedStyle(sub);
      const probe = document.createElement("span");
      probe.style.cssText =
        "position:absolute;visibility:hidden;white-space:nowrap;font-size:100px;";
      probe.style.fontFamily = cs.fontFamily;
      probe.style.fontWeight = cs.fontWeight;
      probe.textContent = sub.textContent;
      document.body.appendChild(probe);
      const naturalWidth = probe.offsetWidth;
      document.body.removeChild(probe);
      if (naturalWidth > 0) {
        const size = Math.max(10, (100 * head.offsetWidth) / naturalWidth);
        sub.style.fontSize = `${size}px`;
      }
    };
    fit();
    if (document.fonts?.ready) document.fonts.ready.then(fit);
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, []);

  return (
    <main className="flex flex-col gap-2.5">
      {/* HERO */}
      <section className="px-2.5">
        <div className="grid grid-cols-1 lg:grid-cols-[2.2fr_1fr] gap-2.5">
          <div className="rounded-2xl bg-[var(--accent)] p-8 sm:p-12 lg:p-14 flex flex-col justify-between min-h-[420px] lg:min-h-[480px] hero-card">
            <div>
              <h1 className="text-black font-bold heading-font text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
                Tervezés.<br />Engedélyezés.<br />Megvalósítás.
              </h1>
              <p className="text-black font-semibold text-base sm:text-lg leading-6 mt-8 max-w-md">
                Komplex építészeti tervezés családi házak és beruházások számára –
                az első gondolattól a kulcsrakész átadásig, végigkísért folyamattal,
                hogy a végeredmény időtálló és magas minőségű építészeti érték legyen.
              </p>
            </div>
            <div className="flex flex-wrap gap-3 mt-10">
              <button
                onClick={scrollToContact}
                className="px-7 py-4 bg-[var(--card-dark)] text-white text-sm font-bold rounded-md hover:bg-[var(--btn-dark-hover)] transition-colors duration-200"
              >
                DíJMENTES KONZULTÁCIÓ
              </button>
              <Link
                to="/munkaink"
                className="px-9 py-4 bg-white text-black text-sm font-bold rounded-md hover:bg-[var(--card-dark)] hover:text-white transition-colors duration-200"
              >
                MUNKÁINK
              </Link>
            </div>
          </div>
          <div className="rounded-2xl overflow-hidden min-h-[300px] lg:min-h-[480px] group">
            <img
              src={wixImg(HERO_IMG, 1200)}
              alt="Modern családi ház – New Idea Architect"
              fetchPriority="high"
              className="w-full h-full object-cover group-hover:scale-[1.03] img-zoom"
            />
          </div>
        </div>
      </section>

      {/* MIÉRT MI */}
      <section className="px-2.5">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.05fr_1.6fr] gap-2.5">
          <div className="rounded-2xl overflow-hidden min-h-[280px] group">
            <img src={wixImg(CORTEN_IMG, 700)} alt="Corten homlokzat" loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] img-zoom" />
          </div>
          <div className="rounded-2xl bg-[var(--why-card)] p-8 sm:p-10 flex flex-col justify-center min-h-[280px]">
            <h2 className="text-white font-bold text-2xl sm:text-[27px] leading-tight mb-4 heading-font">
              Miért a New Idea Architect?
            </h2>
            <p className="text-white text-[15px] leading-7 mb-6">
              Egy építkezés nem rajzokkal bukik el, hanem döntésekkel. Mi abban segítünk,
              hogy ezek a döntések időben, átgondoltan és biztonsággal szülessenek meg.
            </p>
            <ul className="flex flex-col divide-y divide-white/25 border-t border-white/25">
              {["átlátható tervezési folyamat", "reális költségkeretek", "engedélyezési ügyintézés", "kivitelezési támogatás"].map((t) => (
                <li key={t} className="flex items-center gap-3 py-3 text-white text-[15px] font-semibold">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--accent)]">
                    <Check size={13} strokeWidth={3.5} className="text-black" />
                  </span>
                  {t}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl overflow-hidden min-h-[280px] group">
            <img src={wixImg(WHY_RIGHT_IMG, 1000)} alt="Modern lakóház látványterv" loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] img-zoom" />
          </div>
        </div>
      </section>

      {/* SZÁMOKBAN */}
      <section className="px-2.5">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
          {stats.map((s) => (
            <div key={s.label} className="bg-[var(--stat-card)] px-6 py-10 sm:py-14 flex flex-col items-center text-center">
              <span className="text-black heading-font font-bold text-5xl sm:text-6xl lg:text-7xl tracking-tight">{s.value}</span>
              <span className="block w-3/4 border-t border-black mt-5 mb-4" />
              <span className="text-black text-sm sm:text-base font-semibold tracking-wide">{s.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* IDÉZET */}
      <section className="px-2.5">
        <div className="rounded-2xl bg-[var(--card-dark)] border border-white/10 px-8 sm:px-12 py-12 sm:py-16">
          <p className="text-white text-sm font-semibold mb-3">Mies van der Rohe</p>
          <p className="text-white heading-font font-bold text-3xl sm:text-5xl tracking-tight">„Isten a részletekben rejlik.”</p>
        </div>
      </section>

      {/* HOGYAN DOLGOZUNK */}
      <section className="px-2.5">
        <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1.6fr] gap-2.5">
          <div className="rounded-2xl bg-[var(--accent)] p-8 sm:p-12 flex flex-col min-h-[420px]">
            <h2 className="text-black heading-font font-bold text-4xl sm:text-5xl tracking-tight leading-[1.05]">Hogyan dolgozunk?</h2>
            <p className="text-black/80 font-semibold mt-3 mb-10">Egy átgondolt folyamat – meglepetések nélkül</p>
            <div className="flex flex-col divide-y divide-black/20 mt-auto">
              {steps.map((s) => (
                <div key={s.n} className="flex items-start gap-5 py-5 first:pt-0 last:pb-0">
                  <span className="heading-font font-bold text-3xl sm:text-4xl text-black leading-none w-10 shrink-0">{s.n}</span>
                  <div>
                    <p className="text-black font-bold text-base sm:text-lg leading-tight">{s.title}</p>
                    <p className="text-black/80 text-sm leading-6 mt-1.5 max-w-md">{s.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-2xl overflow-hidden min-h-[320px] group">
            <img src={wixImg(WIDE_IMG, 1200)} alt="Társasház látványterv" loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] img-zoom" />
          </div>
        </div>
      </section>

      {/* SZOLGÁLTATÁSOK */}
      <section className="px-2.5">
        <div className="grid grid-cols-1 lg:grid-cols-[1.6fr_1.1fr] gap-2.5">
          <div className="rounded-2xl overflow-hidden min-h-[320px] order-2 lg:order-1 group">
            <img src={wixImg(SERVICES_IMG, 1200)} alt="Modern ház medencével" loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] img-zoom" />
          </div>
          <div className="rounded-2xl bg-[var(--accent)] p-8 sm:p-12 flex flex-col min-h-[420px] order-1 lg:order-2">
            <h2 className="text-black heading-font font-bold text-4xl sm:text-5xl tracking-tight">Szolgáltatások</h2>
            <p className="text-black/80 font-semibold mt-3 mb-8">Komplex építészeti szolgáltatások egy kézből</p>
            <ul className="flex flex-col divide-y divide-black/20">
              {services.map((s, i) => (
                <li key={s} className="flex items-center gap-4 py-3 text-black text-sm sm:text-base font-medium first:pt-0">
                  <span className="heading-font font-bold text-xs text-black/60 w-6 shrink-0">0{i + 1}</span>
                  <span className="capitalize-first">{s}</span>
                </li>
              ))}
            </ul>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-auto pt-10">
              <p className="text-black font-bold text-sm max-w-[220px] leading-5">
                Minden projektet egyedileg kezelünk – sablonok nélkül
              </p>
              <button
                onClick={scrollToContact}
                className="px-7 py-4 bg-[var(--card-dark)] text-white text-sm font-bold rounded-md hover:bg-[var(--btn-dark-hover)] transition-colors duration-200 shrink-0"
              >
                DíJMENTES KONZULTÁCIÓ
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* REFERENCIÁINK */}
      <section className="px-2.5">
        <div className="rounded-2xl bg-[var(--card-dark)] border border-white/10 p-8 sm:p-12 mb-2.5">
          <p className="text-white text-sm font-semibold mb-3">Referenciáink</p>
          <h2 className="text-white heading-font font-bold text-3xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
            Professzionális építészeti tervezés -<br />kompromisszumok nélkül
          </h2>
          <Link
            to="/munkaink"
            className="inline-block mt-8 px-9 py-4 border border-white text-white text-sm font-bold rounded-md hover:bg-white hover:text-black transition-colors duration-200"
          >
            MUNKÁINK
          </Link>
        </div>
        <div className="columns-2 md:columns-3 lg:columns-4 gap-2.5">
          {lakohazak.slice(0, 12).map((p, i) => (
            <Link
              key={p.slug}
              to={`/portfolio/lakohazak/${p.slug}`}
              className="relative block mb-2.5 break-inside-avoid rounded-xl overflow-hidden group bg-[#1a1a1a]"
            >
              <img
                src={wixImg(p.cover, 600)}
                alt={p.name}
                loading="lazy"
                decoding="async"
                className="w-full h-auto block"
              />
              <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-4 pt-12 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <span className="text-white font-bold text-sm sm:text-base">{p.name}</span>
              </span>
              <span className="absolute inset-0 rounded-xl ring-0 group-hover:ring-4 ring-[var(--accent)] ring-inset transition-shadow duration-200 pointer-events-none" />
            </Link>
          ))}
        </div>
      </section>

      {/* TERVEZÉSI ALAPELVEK */}
      <section className="px-2.5">
        <div className="rounded-2xl bg-[var(--card-dark)] border border-white/10 p-8 sm:p-12">
          <div className="inline-block mb-10">
            <h2 ref={headRef} className="text-white heading-font font-bold text-4xl sm:text-5xl tracking-tight whitespace-nowrap inline-block">
              Tervezési alapelvek
            </h2>
            <p
              ref={subRef}
              className="text-white/70 font-bold whitespace-nowrap leading-none mt-2.5"
            >
              KIEMELKEDŐ TERVEZÉSI MEGOLDÁSOK
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {principles.map((p, i) => (
              <div key={p.title} className="border-t border-white/40 pt-5">
                <p className="text-[var(--accent)] font-bold text-sm mb-3">0{i + 1}</p>
                <h3 className="text-white font-bold text-base leading-5 mb-3 heading-font">{p.title}</h3>
                <p className="text-white/75 text-sm leading-6">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* KAPCSOLAT */}
      <ContactSection />
    </main>
  );
};

export default Home;
